import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, RUN_TASK_INSTANCES_PAGE_SIZE, TASK_INSTANCES_LIMIT } from '../../constants';
import { AirflowTaskInstance } from '../../types';

type ConfirmAction = { type: 'success' | 'failed' | 'delete'; task: AirflowTaskInstance };

/*
 * Manages the run-scoped Task Instances tab: fetches every instance for the run (paging
 * through the server in TASK_INSTANCES_LIMIT chunks, only `state` is server-filtered), then
 * filters (task ID/map index/try number/operator) and paginates entirely client-side
 */
export function useTaskInstancesTab(dagId: string, runId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();

    const [allInstances, setAllInstances] = useState<AirflowTaskInstance[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [stateFilter, setStateFilter] = useState('');
    const [taskIdFilter, setTaskIdFilter] = useState('');
    const [operatorFilter, setOperatorFilter] = useState('');
    const [mapIndexFilter, setMapIndexFilter] = useState('');
    const [tryNumberFilter, setTryNumberFilter] = useState('');
    const [draftFilters, setDraftFilters] = useState({ taskId: '', operator: '', mapIndex: '', tryNumber: '' });
    const [page, setPage] = useState(1);
    const [clearDialogTask, setClearDialogTask] = useState<AirflowTaskInstance | null>(null);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

    /*
     * Pages through every task instance of the run, applying only the server-side state filter
     */
    const fetchAllInstances = useCallback(async () => {
        setIsLoading(true);
        try {
            let offset = 0;
            let collected: AirflowTaskInstance[] = [];
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const response = await AirflowDashboardHandler.listRunTaskInstances(Env, dagId, runId, {
                    limit: TASK_INSTANCES_LIMIT,
                    offset,
                    order_by: '-start_date',
                    state: stateFilter ? [stateFilter] : undefined,
                });
                collected = collected.concat(response.task_instances);
                const total = response.total_entries ?? collected.length;
                if (response.task_instances.length < TASK_INSTANCES_LIMIT || collected.length >= total) break;
                offset += TASK_INSTANCES_LIMIT;
            }
            setAllInstances(collected);
        } catch {
            errorAlert('Failed to load task instances.');
        } finally {
            setIsLoading(false);
        }
    }, [Env, dagId, runId, stateFilter]);

    useEffect(() => {
        fetchAllInstances();
    }, [fetchAllInstances, refreshKey]);

    const filteredInstances = useMemo(() => {
        return allInstances.filter((task) => {
            if (taskIdFilter && !task.task_id.toLowerCase().includes(taskIdFilter.toLowerCase())) return false;
            if (operatorFilter && !(task.operator_name ?? task.operator ?? '').toLowerCase().includes(operatorFilter.toLowerCase())) return false;
            if (mapIndexFilter && String(task.map_index) !== mapIndexFilter) return false;
            if (tryNumberFilter && String(task.try_number) !== tryNumberFilter) return false;
            return true;
        });
    }, [allInstances, taskIdFilter, operatorFilter, mapIndexFilter, tryNumberFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredInstances.length / RUN_TASK_INSTANCES_PAGE_SIZE));
    const pagedInstances = useMemo(
        () => filteredInstances.slice((page - 1) * RUN_TASK_INSTANCES_PAGE_SIZE, page * RUN_TASK_INSTANCES_PAGE_SIZE),
        [filteredInstances, page]
    );

    const hasActiveFilters = Boolean(taskIdFilter || operatorFilter || mapIndexFilter || tryNumberFilter || stateFilter);

    const handleStateFilterChange = useCallback((value: string) => {
        setStateFilter(value);
        setPage(1);
    }, []);

    const handleOpenFilters = useCallback(() => {
        setDraftFilters({ taskId: taskIdFilter, operator: operatorFilter, mapIndex: mapIndexFilter, tryNumber: tryNumberFilter });
    }, [taskIdFilter, operatorFilter, mapIndexFilter, tryNumberFilter]);

    const handleDraftChange = useCallback((key: keyof typeof draftFilters, value: string) => {
        setDraftFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleApplyFilters = useCallback(() => {
        setTaskIdFilter(draftFilters.taskId);
        setOperatorFilter(draftFilters.operator);
        setMapIndexFilter(draftFilters.mapIndex);
        setTryNumberFilter(draftFilters.tryNumber);
        setPage(1);
    }, [draftFilters]);

    const handleClearFilters = useCallback(() => {
        setTaskIdFilter('');
        setOperatorFilter('');
        setMapIndexFilter('');
        setTryNumberFilter('');
        setStateFilter('');
        setPage(1);
    }, []);

    /*
     * Executes the confirmed mark-success/mark-failed/delete action for a task instance
     */
    const handleConfirmTaskAction = useCallback(async () => {
        if (!confirmAction) return;
        const { type, task } = confirmAction;
        try {
            if (type === 'delete') {
                await AirflowDashboardHandler.deleteTaskInstance(Env, dagId, runId, task.task_id);
                successAlert(`Deleted task instance "${task.task_id}".`);
            } else {
                await AirflowDashboardHandler.patchTaskInstance(Env, dagId, runId, task.task_id, { new_state: type });
                successAlert(`Marked "${task.task_id}" as ${type}.`);
            }
            setConfirmAction(null);
            fetchAllInstances();
        } catch {
            errorAlert(`Failed to ${type} task "${task.task_id}".`);
        }
    }, [Env, dagId, runId, confirmAction, fetchAllInstances]);

    /*
     * Navigates to a task instance's detail page
     */
    const handleTaskClick = useCallback(
        (task: AirflowTaskInstance) => {
            const mapIndexQuery = task.map_index !== undefined && task.map_index >= 0 ? `?map_index=${task.map_index}` : '';
            history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}/runs/${encodeURIComponent(runId)}/tasks/${encodeURIComponent(task.task_id)}${mapIndexQuery}`);
        },
        [history, dagId, runId]
    );

    return {
        isLoading,
        stateFilter,
        draftFilters,
        hasActiveFilters,
        pagedInstances,
        filteredCount: filteredInstances.length,
        page,
        setPage,
        totalPages,
        clearDialogTask,
        setClearDialogTask,
        confirmAction,
        setConfirmAction,
        handleStateFilterChange,
        handleOpenFilters,
        handleDraftChange,
        handleApplyFilters,
        handleClearFilters,
        handleConfirmTaskAction,
        handleTaskClick,
        refetch: fetchAllInstances,
    };
}
